import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  videoElementAtom,
  videoMutedAtom,
  videoPlaybackAtom,
} from '../atoms/videoPlayerAtoms';

/**
 * Create a ref to be attached to a video element that will be used by
 * the video player. This hook handles the video element setup and event listeners.
 *
 * @returns React ref to be attached to a video element
 */
export function useVideoPlayerRef() {
  const [videoElement, setVideoElement] = useAtom(videoElementAtom);
  const videoRef = useRef<HTMLVideoElement>(null);

  const isMuted = useAtomValue(videoMutedAtom);
  const [, setPlayback] = useAtom(videoPlaybackAtom);

  const TIME_UPDATE_THROTTLE = 100; // Throttle time updates for better performance
  const lastTimeUpdateRef = useRef(0);

  // Attach ref to atom when component mounts
  useEffect(() => {
    if (videoRef.current && videoRef.current !== videoElement) {
      setVideoElement(videoRef.current);
    }
  }, [videoElement, setVideoElement]);

  // Apply mute settings to the video element
  useEffect(() => {
    if (videoElement) {
      videoElement.muted = isMuted;
    }
  }, [videoElement, isMuted]);

  // Set up video element event listeners
  useEffect(() => {
    if (!videoElement) {
      return;
    }

    // Update current time as video plays, throttled to avoid excessive updates
    const handleTimeUpdate = () => {
      const now = Date.now();

      if (now - lastTimeUpdateRef.current < TIME_UPDATE_THROTTLE) {
        return;
      }

      lastTimeUpdateRef.current = now;

      setPlayback((prev) => ({
        ...prev,
        currentTime: videoElement.currentTime,
      }));
    };

    // Update duration when it becomes available
    const handleDurationChange = () => {
      const newDuration = videoElement.duration || 0;

      setPlayback((prev) => ({
        ...prev,
        duration: newDuration,
      }));
    };

    // Update playing state when video starts playing
    const handlePlay = () => {
      setPlayback((prev) => ({
        ...prev,
        isPlaying: true,
      }));
    };

    // Update playing state when video pauses
    const handlePause = () => {
      setPlayback((prev) => ({
        ...prev,
        isPlaying: false,
      }));
    };

    // Update playing state when video ends
    const handleEnded = () => {
      setPlayback((prev) => ({
        ...prev,
        isPlaying: false,
      }));
    };

    // Attach all event listeners
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('durationchange', handleDurationChange);
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);

    // Cleanup function to remove all event listeners
    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('durationchange', handleDurationChange);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [videoElement, setPlayback]);

  return videoRef;
}
